import * as React from "react"

import { cn } from "@/lib/utils"
import { Checkbox } from "./checkbox"
import { Skeleton } from "./skeleton"

// Table Context for shared options
interface TableContextValue {
  striped?: boolean;
  hoverable?: boolean;
  selectable?: boolean;
  selectedRows?: Set<string>;
  onSelectRow?: (id: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  allSelected?: boolean;
  someSelected?: boolean;
}

const TableContext = React.createContext<TableContextValue>({});

const useTableContext = () => React.useContext(TableContext);

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  striped?: boolean;
  hoverable?: boolean;
  selectable?: boolean;
  selectedRows?: Set<string>;
  onSelectRow?: (id: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({
    className,
    striped = false,
    hoverable = true,
    selectable = false,
    selectedRows,
    onSelectRow,
    onSelectAll,
    children,
    ...props
  }, ref) => {
    // Calculate selection state for header checkbox
    const allSelected = selectedRows ? selectedRows.size > 0 : false;
    const someSelected = selectedRows ? selectedRows.size > 0 : false;

    return (
      <TableContext.Provider
        value={{
          striped,
          hoverable,
          selectable,
          selectedRows,
          onSelectRow,
          onSelectAll,
          allSelected,
          someSelected,
        }}
      >
        <div className="relative w-full overflow-auto">
          <table
            ref={ref}
            className={cn("w-full caption-bottom text-sm", className)}
            {...props}
          >
            {children}
          </table>
        </div>
      </TableContext.Provider>
    );
  }
)
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  loading?: boolean;
  loadingRows?: number;
  loadingColumns?: number;
}

const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, loading, loadingRows = 5, loadingColumns = 4, children, ...props }, ref) => {
    const { striped, selectable } = useTableContext();

    if (loading) {
      const cols = selectable ? loadingColumns + 1 : loadingColumns;
      return (
        <tbody
          ref={ref}
          className={cn("[&_tr:last-child]:border-0", className)}
          {...props}
        >
          {Array.from({ length: loadingRows }).map((_, rowIndex) => (
            <tr key={rowIndex} className="border-b">
              {Array.from({ length: cols }).map((_, colIndex) => (
                <td key={colIndex} className="p-2">
                  <Skeleton className="h-5 w-full animate-pulse bg-muted rounded" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      );
    }

    return (
      <tbody
        ref={ref}
        className={cn(
          "[&_tr:last-child]:border-0",
          striped && "[&_tr:nth-child(even)]:bg-muted/50",
          className
        )}
        {...props}
      >
        {children}
      </tbody>
    );
  }
)
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  rowId?: string;
  clickable?: boolean;
  onRowClick?: () => void;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, rowId, clickable, onRowClick, children, ...props }, ref) => {
    const { hoverable, selectable, selectedRows, onSelectRow } = useTableContext();
    const isSelected = rowId && selectedRows ? selectedRows.has(rowId) : false;

    return (
      <tr
        ref={ref}
        data-state={isSelected ? "selected" : undefined}
        className={cn(
          "border-b transition-colors",
          hoverable && "hover:bg-muted/50",
          clickable && "cursor-pointer",
          "data-[state=selected]:bg-muted",
          className
        )}
        onClick={onRowClick}
        {...props}
      >
        {selectable && rowId && (
          <td className="p-2 w-10">
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onSelectRow?.(rowId, !!checked)}
              onClick={(e) => e.stopPropagation()}
            />
          </td>
        )}
        {children}
      </tr>
    );
  }
)
TableRow.displayName = "TableRow"

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  showSelectAll?: boolean;
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, showSelectAll, children, ...props }, ref) => {
    const { selectable, onSelectAll, allSelected } = useTableContext();

    return (
      <>
        {selectable && showSelectAll && (
          <th className="h-10 px-2 w-10">
            <Checkbox
              checked={allSelected}
              // indeterminate state could be added here
              onCheckedChange={(checked) => onSelectAll?.(!!checked)}
            />
          </th>
        )}
        <th
          ref={ref}
          className={cn(
            "h-10 px-2 text-start align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pe-0 [&>[role=checkbox]]:translate-y-[2px]",
            className
          )}
          {...props}
        >
          {children}
        </th>
      </>
    );
  }
)
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle text-start [&:has([role=checkbox])]:pe-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

// Loading Skeleton Component for Tables
interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
}

function TableSkeleton({
  rows = 5,
  columns = 4,
  showHeader = true
}: TableSkeletonProps) {
  return (
    <Table>
      {showHeader && (
        <TableHeader>
          <TableRow>
            {Array.from({ length: columns }).map((_, i) => (
              <TableHead key={i}>
                <Skeleton className="h-4 w-20 animate-pulse bg-muted rounded" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      )}
      <TableBody loading loadingRows={rows} loadingColumns={columns} />
    </Table>
  );
}
TableSkeleton.displayName = "TableSkeleton"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableSkeleton,
}
